/**
 * Draws a layer through effects that work on its finished picture: a blur
 * filter, and motion blur, which decides where and how often the picture
 * lands.
 *
 * Owns: the hook on a Fabric object's `render`, the picture it draws the layer
 * into, and the plain draw of that picture. Does not own: any effect itself.
 * Presets mount and drive their effects through `setPictureFilter` and
 * `setPictureComposer`.
 *
 * The one thing to know: an effect never draws the layer. The layer is drawn
 * once per frame into its picture, the same bitmap Fabric keeps as the
 * object's cache (Fabric's own cache is reused when the object has one).
 * Filters return a changed picture, and a composer places it. With nothing
 * active the hook steps aside and Fabric renders as usual, so a mounted
 * effect costs nothing while it is idle.
 *
 * Like the other borrowed state, the hook lives on the object instance and
 * never in the layer: `releaseRenderEffects` takes it off when the timeline is
 * torn down.
 */
import { Group } from 'fabric';
import type { FabricObject } from 'fabric';
import type { CachedFabricObject } from '@/types/canvas/fabric';
import type {
  LayerPicture,
  PictureComposer,
  PictureFilter,
  PictureFilterKey,
  RenderEffects
} from '@/types/layers/animationRuntime';

/**
 * Pixels Fabric adds around a cache bitmap so edges are not clipped (its
 * ALIASING_LIMIT, which it does not export).
 */
export const CACHE_PADDING_PX = 2;

/** The effects mounted on each object. */
const mounted = new WeakMap<object, RenderEffects>();

/** Pictures drawn here for objects Fabric does not cache, reused per frame. */
const pictureCanvases = new WeakMap<object, HTMLCanvasElement>();

/*
 * Public
 */

/** Mount `filter` on `obj` under `key`, replacing any there; null removes it. */
export function setPictureFilter(
  obj: FabricObject,
  key: PictureFilterKey,
  filter: PictureFilter | null
): void {
  if (!filter) {
    const effects = mounted.get(obj);
    if (!effects) return;
    effects.filters.delete(key);
    unmountIfEmpty(obj, effects);
    return;
  }
  mount(obj).filters.set(key, filter);
}

/** Mount `composer` on `obj`, replacing any there; null removes it. */
export function setPictureComposer(
  obj: FabricObject,
  composer: PictureComposer | null
): void {
  if (!composer) {
    const effects = mounted.get(obj);
    if (!effects) return;
    effects.composer = null;
    unmountIfEmpty(obj, effects);
    return;
  }
  mount(obj).composer = composer;
}

/** Take every effect off `obj` and give its own `render` back. */
export function releaseRenderEffects(obj: object): void {
  const effects = mounted.get(obj);
  if (!effects) return;
  effects.restore();
  mounted.delete(obj);
  pictureCanvases.delete(obj);
}

/**
 * Draw a picture in the layer's own plane: `ctx` already carries the layer's
 * transform. This is Fabric's own step for drawing an object's cache.
 */
export function drawPicture(
  ctx: CanvasRenderingContext2D,
  picture: LayerPicture
): void {
  ctx.scale(1 / picture.zoomX, 1 / picture.zoomY);
  ctx.drawImage(picture.canvas, -picture.originX, -picture.originY);
}

/*
 * Helpers
 */

/**
 * The effects on `obj`, hooking its `render` the first time.
 *
 * The hook follows Fabric's own render step for step — composite operation,
 * shadow, transform, opacity, then the cached bitmap — so a picture drawn
 * plainly lands exactly where Fabric would put it. A group also raises
 * `_transformDone` for the whole step, as `Group.render` does, so its
 * children draw in its plane instead of applying its transform a second
 * time. The off-screen skip is left out on purpose: a blur or a motion streak
 * can reach the screen while the layer's own box is still off it.
 */
function mount(obj: FabricObject): RenderEffects {
  const existing = mounted.get(obj);
  if (existing) return existing;

  const hadOwnRender = Object.prototype.hasOwnProperty.call(obj, 'render');
  const render = obj.render;
  const effects: RenderEffects = {
    filters: new Map(),
    composer: null,
    restore: () => {
      if (hadOwnRender) obj.render = render;
      else delete (obj as Partial<FabricObject>).render;
    }
  };

  obj.render = function (
    this: FabricObject,
    ctx: CanvasRenderingContext2D
  ): void {
    const filters = [...effects.filters.values()].filter(f => f.isActive());
    const composer = effects.composer?.isActive() ? effects.composer : null;
    if ((filters.length === 0 && !composer) || this.isNotVisible()) {
      render.call(this, ctx);
      return;
    }
    const isGroup = this instanceof Group;
    if (isGroup) this._transformDone = true;
    let picture = takePicture(this, ctx);
    for (const filter of filters) picture = filter.apply(picture, this);
    ctx.save();
    this._setupCompositeOperation(ctx);
    this.drawSelectionBackground(ctx);
    this._setShadow(ctx);
    if (composer) {
      composer.draw(ctx, picture, this);
    } else {
      this.transform(ctx);
      this._setOpacity(ctx);
      drawPicture(ctx, picture);
    }
    ctx.restore();
    if (isGroup) this._transformDone = false;
  };

  mounted.set(obj, effects);
  return effects;
}

function unmountIfEmpty(obj: FabricObject, effects: RenderEffects): void {
  if (effects.filters.size === 0 && !effects.composer) {
    releaseRenderEffects(obj);
  }
}

/**
 * Draw `obj` into its picture for this frame, for drawing into `target`. An
 * object that keeps a Fabric cache hands over that cache, refreshed only if
 * it is stale. Any other is drawn into a canvas kept here, sized and placed
 * the way Fabric sizes a cache, so both kinds come out the same.
 */
function takePicture(
  obj: FabricObject,
  target: CanvasRenderingContext2D
): LayerPicture {
  if (obj.shouldCache()) {
    const cached = obj as CachedFabricObject;
    // a zoom or size change redraws too, which the picture's zoom and size show
    const redrawn = cached.dirty || !!cached.clipPath?.absolutePositioned;
    cached.renderCache();
    return {
      canvas: cached._cacheCanvas,
      zoomX: cached.zoomX,
      zoomY: cached.zoomY,
      originX: cached.cacheTranslationX,
      originY: cached.cacheTranslationY,
      redrawn
    };
  }

  const dims = pictureDimensions(obj, target);
  const canvas = pictureCanvases.get(obj) ?? document.createElement('canvas');
  pictureCanvases.set(obj, canvas);
  if (canvas.width !== dims.width || canvas.height !== dims.height) {
    canvas.width = dims.width;
    canvas.height = dims.height;
  }
  const ctx = canvas.getContext('2d')!;
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  const originX = Math.round(dims.width / 2 - dims.x / 2) + dims.x / 2;
  const originY = Math.round(dims.height / 2 - dims.y / 2) + dims.y / 2;
  ctx.translate(originX, originY);
  ctx.scale(dims.zoomX, dims.zoomY);
  obj.drawObject(ctx, false, {
    zoomX: dims.zoomX,
    zoomY: dims.zoomY,
    cacheTranslationX: originX,
    cacheTranslationY: originY,
    width: dims.width,
    height: dims.height,
    parentClipPaths: []
  });
  obj.dirty = false;
  return {
    canvas,
    zoomX: dims.zoomX,
    zoomY: dims.zoomY,
    originX,
    originY
  };
}

/**
 * How big to draw `obj`'s picture, and at what zoom. Fabric sizes a bitmap
 * from the object's scaling on its own canvas. An object on no canvas — the
 * letters of a split-up text, which its overlay draws — would come out at its
 * bare scale, too soft on a zoomed or retina canvas, so the context it is
 * drawn into sets the zoom instead.
 */
function pictureDimensions(
  obj: FabricObject,
  target: CanvasRenderingContext2D
): ReturnType<FabricObject['_getCacheCanvasDimensions']> {
  const dims = obj._getCacheCanvasDimensions();
  if (obj.canvas) return obj._limitCacheSize(dims);
  const m = target.getTransform().multiply(new DOMMatrix(obj.calcOwnMatrix()));
  const zoomX = Math.hypot(m.a, m.b);
  const zoomY = Math.hypot(m.c, m.d);
  const x = dims.zoomX ? (dims.x / dims.zoomX) * zoomX : 0;
  const y = dims.zoomY ? (dims.y / dims.zoomY) * zoomY : 0;
  return obj._limitCacheSize({
    ...dims,
    zoomX,
    zoomY,
    x,
    y,
    width: Math.ceil(x + CACHE_PADDING_PX),
    height: Math.ceil(y + CACHE_PADDING_PX)
  });
}
